import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Stepper, Step, StepLabel,
  List, ListItem, ListItemText, CircularProgress, Alert
} from '@mui/material';
import { Facebook as FacebookIcon, Sync as SyncIcon } from '@mui/icons-material';
import apiClient from '../../api/client';

const MetaIntegration: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const steps = ['Select Business', 'Select Page', 'Enable Lead Sync'];

  const fetchBusinesses = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/v1/meta/graph/businesses');
      setBusinesses(response.data);
    } catch (err: any) {
      setError("Failed to fetch businesses. Ensure Meta account is connected.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async (businessId: string) => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/v1/meta/graph/pages', { params: { businessId } });
      setPages(response.data);
      setActiveStep(1);
    } catch (err: any) {
      setError("Failed to fetch pages.");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPage = async (pageId: string) => {
    setLoading(true);
    try {
      // Mock access token as it's typically handled via popup redirect, but here we assume existing flow
      await apiClient.post(`/api/v1/meta/business/pages/${pageId}/connect`, null, {
        params: { accessToken: 'VALIDATED_IN_BACKEND' }
      });
      setActiveStep(2);
    } catch (err: any) {
      setError("Failed to connect page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <FacebookIcon color="primary" fontSize="large" />
          <Typography variant="h5" fontWeight="bold">Meta Business Integration</Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

        {!loading && activeStep === 0 && (
          <List>
            <Typography variant="subtitle2" sx={{ px: 2, mb: 1 }}>Available Business Managers</Typography>
            {businesses.map((biz) => (
              <ListItem key={biz.id} divider>
                <ListItemText primary={biz.name} secondary={`ID: ${biz.id}`} />
                <Button variant="outlined" onClick={() => fetchPages(biz.id)}>Select</Button>
              </ListItem>
            ))}
          </List>
        )}

        {!loading && activeStep === 1 && (
          <List>
            <Typography variant="subtitle2" sx={{ px: 2, mb: 1 }}>Available Facebook Pages</Typography>
            {pages.map((page) => (
              <ListItem key={page.id} divider>
                <ListItemText primary={page.name} secondary={`Category: ${page.category}`} />
                <Button variant="contained" onClick={() => handleConnectPage(page.id)}>Connect</Button>
              </ListItem>
            ))}
          </List>
        )}

        {!loading && activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SyncIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6">Lead Sync Active</Typography>
            <Typography color="textSecondary" sx={{ mb: 4 }}>
              Spark CRM is now listening for real-time lead events from your Meta forms.
            </Typography>
            <Button variant="outlined" onClick={() => setActiveStep(0)}>Configure Another Page</Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MetaIntegration;
